import React, { useEffect, useRef, useState } from 'react';
import Layout from '../../components/Layout/Layout.component';
import Pagination from '../../components/Pagination.component';
import { TableSelection } from '../../components/SelectableTable/SelectableTable.types';
import { SelectableTable } from '../../components/SelectableTable/SelectableTable.component';
import { Paths } from '../../utils/routerUrls';
import {
  deactivateAllUsers,
  deactivateSelectedUsers,
  deleteUser,
  exportUsersApi,
  forcePasswordChangeSelectedUsers,
  passwordChangeAllUsers,
} from '../../services/users/users.service';
import { AccountTypeEnum, getAccountTypeLabel, IUser, UserDeleteStatus } from '../../models/user.types';
import { Typography } from '@material-ui/core';
import Breadcrumbs from '../../components/Breadcrumbs.component';
import { useStyles } from '../styles';
import UserCreate from '../User/UserCreate/UserCreate.component';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import { ItemsFilter } from '../../components/SelectableTable/filters/ItemsFilter.component';
import { UsersTableItem, ExaminersTableItem, UsersSortBy, ExaminersSortBy } from './Users.types';
import { ActionMenuItem } from '../../components/ActionsMenu/ActionsMenu.types';
import ActionsMenu from '../../components/ActionsMenu/ActionsMenu.component';
import UserDetails from '../User/UserDetails/UserDetails.component';
import { RolesForUser } from '../../components/Roles/RolesForUser/RolesForUser.component';
import { SelectedUser } from '../../services/users/users.types';
import { SelectedExaminer } from '../../services/examiners/examiners.types';
import ExaminerCreate from '../Examiner/ExaminerCreate/ExaminerCreate.component';
import ExaminerDialog from '../Examiner/ExaminerDialog/ExaminerDialog.component';
import _ from 'lodash';
import usePermission, { UserPermission } from '../../hooks/usePermission';
import { useLayoutContext } from '../../Context/Layout.context';
import { useTranslation } from 'react-i18next';
import {
  convertToFilterLabelItems,
  getExamCommitteeNameFromIdString,
} from '../SampleAssessmentRepo/Helpers/AccountTypeRendering';
import { ImportExaminers } from '../Examiner/Import/ImportExaminers.component';
import { Prompt } from '../../components/Prompt/Prompt.component';
import TooltipOne from '../../components/TooltipOne/TooltipOne.component';
import { NewMessageModal } from '../../components/NewMessage/NewMessageModal.component';
import DotWithLabel from '../../components/DotWithLabel.component';
import OutlinedButton from '../../components/buttons/OutlinedButton.component';
import { SelectedRecordCountDisplay } from '../../components/SelectableTable/SelectedRecordCountDisplay/SelectedRecordCountDisplay.component';
import { exportFile } from '../../utils/servicesHelper';
import { formatPhone } from '../../components/UserProfile/helpers';
import useSessionUser from '../../hooks/useSessionUser';
import { useUsersTableData } from './useUserTableData';
import { useUsersTableColumns } from './useUsersTableColumns';
import { EditUserPasswordByAdmin } from '../../components/UserProfile/EditUserPasswordByAdmin';

export type IndexedUsers = { [key: string]: UsersTableItem };

const Users: React.FC = () => {
  const classes = useStyles();

  const { t } = useTranslation();
  const { user } = useSessionUser();
  const { showLoader, openAlert, isSidebarMinimized: isSidebarMinimazed, isLoader } = useLayoutContext();

  const {
    isDescending,
    setIsDescending,
    sortByColumnId,
    setSortByColumnId,
    sortExaminersByColumnId,
    setSortExaminersByColumnId,
    filterState,
    users,
    pageInfo,
    filter,
    getFilterValues,
    getUsers,
    examinersRoute,
    getExaminers,
    uncheckAllUsersFilters,
    handleFilterChange,
    getPages,
    userAdded,
    uncheckUsersFilterItem,
  } = useUsersTableData();

  const hasAdminOkeExaminerReadPermission = usePermission(UserPermission.AdminOkeExaminerRead);
  const hasAdminExaminerReadPermission = usePermission(UserPermission.AdminExaminerRead);
  const hasAdminOkeUserReadPermission = usePermission(UserPermission.AdminOkeUserRead);
  const hasAdminUserReadPermission = usePermission(UserPermission.AdminUserRead);

  const [usersData, setUsersData] = useState<UsersTableItem[]>([]);
  const [examinersData, setExaminersData] = useState<ExaminersTableItem[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined);
  const [isExaminerEdit, setIsExaminerEdit] = useState<boolean>(false);
  const [selectedExaminerId, setSelectedExaminerId] = useState<string | undefined>(undefined);
  const [roleData, setRoleData] = useState<{ id: string; userName: string } | undefined>(undefined);
  const [openExaminerModal, setOpenExaminerModal] = useState<boolean>(false);
  const [openUserDetailsModal, setOpenUserDetailsModal] = useState<boolean>(false);
  const [isUsersDeactivateConfirmationModal, setIsUsersDeactivateConfirmationModal] = useState<boolean>(false);
  const [isUserDeleteConfirmationModal, setIsUserDeleteConfirmationModal] = useState<boolean>(false);

  const [isForceUsersPasswordChangeConfirmationModal, setIsForceUsersPasswordChangeConfirmationModal] =
    useState<boolean>(false);
  const [isAllUsersDeactivateConfirmationModal, setIsAllUsersDeactivateConfirmationModal] = useState<boolean>(false);
  const [isAllUsersForcePasswordChangeConfirmationModal, setIsAllUsersForcePasswordChangeConfirmationModal] =
    useState<boolean>(false);
  const [selectedRecipients, setSelectedRecipients] = useState<SelectedUser[] | SelectedExaminer[] | undefined>(
    undefined,
  );
  const [usersSelection, setUsersSelection] = useState<TableSelection>({ items: [] });
  const hasAdminUserRolesManagePermission = usePermission(UserPermission.AdminUserRolesManage);
  const hasAdminExaminerCreatePerm = usePermission(UserPermission.AdminExaminerCreate);
  const hasAdminUserCreate = usePermission(UserPermission.AdminUserCreate);
  const hasAdminOKEExaminerCreatePerm = usePermission(UserPermission.AdminOkeExaminerCreate);
  const hasAdminUserDeactivationPermission = usePermission(UserPermission.AdminUserDeactivation);
  const hasAdminUserPasswordChangePermission = usePermission(UserPermission.AdminUserPasswordChange);
  const hasAdminOKEUserPasswordChange = usePermission(UserPermission.AdminOkeUserPasswordChange);
  const hasPermissionAdminCommunicationAccess = usePermission(UserPermission.AdminCommunicationAccess);
  const hasPermissionAdminOkeCommunicationAccess = usePermission(UserPermission.AdminOkeCommunicationAccess);
  const hasPermissionExaminerCommunicationAccess = usePermission(UserPermission.ExaminerCommunicationAccess);
  const hasAdminPermission = user.profile.role === 'Administrator';

  const selectedUsersData = useRef<IndexedUsers>({});

  const [openAddUserPassword, setOpenAddUserPassword] = useState<boolean>(false);
  const [selectedUserForPasswordActivation, setSelectedUserForPasswordActivation] = useState<IUser | undefined>(
    undefined,
  );

  const handleAddUserPasswordClose = () => {
    setOpenAddUserPassword(false);
    setSelectedUserForPasswordActivation(undefined);
  };

  useEffect(() => {
    Object.values(selectedUsersData.current).forEach((item) => {
      if (!usersSelection.items.includes(item.id)) {
        delete selectedUsersData.current[item.id];
      }
    });

    usersSelection.items.forEach((item) => {
      if (!selectedUsersData.current[item]) {
        const userData = usersData.find((user) => user.id === item);
        if (userData) {
          selectedUsersData.current[item] = userData;
        }
      }
    });
  }, [usersData, usersSelection]);

  const { usersColumns, examinersColumns } = useUsersTableColumns();

  const hasPermissionToCommunicationModule =
    hasPermissionAdminCommunicationAccess ||
    hasPermissionAdminOkeCommunicationAccess ||
    hasPermissionExaminerCommunicationAccess;

  const [messageModal, setMessageModal] = useState<{
    open: boolean;
    type: 'standard' | 'helpdesk';
    shouldSessionDataBeHide: boolean;
  }>({
    open: false,
    type: 'standard',
    shouldSessionDataBeHide: true,
  });

  const exportUsers = (examinerRoute: boolean) => {
    const abortController = new AbortController();
    const { signal } = abortController;
    const forceAccountType = examinerRoute ? AccountTypeEnum.Examiner : undefined;
    const filters = getFilterValues(forceAccountType);
    showLoader(true, 'Eksportuję dane');
    exportUsersApi(examinerRoute, signal ?? undefined, sortByColumnId, isDescending, '', ...(filters as string[]))
      .then(
        (response: Blob) => {
          exportFile(response, forceAccountType === AccountTypeEnum.Examiner ? 'wykaz_egzaminatorow' : 'uzytkownicy');
        },
        () => {
          openAlert('error', 'Eksport danych nie powiódł się');
        },
      )
      .finally(() => showLoader(false));
  };

  const onUsersActionsClick = (id: string) => {
    setSelectedUserId(id);
    setOpenUserDetailsModal(true);
  };

  const getUserActionsMenuIcon = (data: IUser, rowIndex: number): JSX.Element => {
    const items: ActionMenuItem[] = [];
    items.push({
      text: 'Szczegóły',
      onClick: () => {
        onUsersActionsClick(data.id);
      },
    });
    if (hasAdminUserRolesManagePermission) {
      items.push({
        text: 'Zarządzaj rolami',
        onClick: () => setRoleData({ id: data.id, userName: `${data.firstName} ${data.lastName}` }),
      });
    }
    if (hasAdminPermission && !data.emailConfirmed) {
      items.push({
        text: 'Aktywuj konto',
        onClick: () => {
          setSelectedUserForPasswordActivation(data);
          setOpenAddUserPassword(true);
        },
      });
    }

    if (hasAdminUserDeactivationPermission) {
      items.push({
        text: 'Ponowna aktywacja konta',
        disabled: false,
        onClick: () => {
          setSelectedUserId(data.id);
          setIsUsersDeactivateConfirmationModal(true);
        },
      });
    }
    if (hasAdminUserPasswordChangePermission || hasAdminOKEUserPasswordChange) {
      items.push({
        text: 'Wymuś zmianę hasła',
        disabled: false,
        onClick: () => {
          setSelectedUserId(data.id);
          setIsForceUsersPasswordChangeConfirmationModal(true);
        },
      });
    }
    if (hasPermissionToCommunicationModule && user.profile.sub !== data.id) {
      items.push({
        text: 'Wyślij wiadomość',
        onClick: () => {
          setSelectedRecipients([
            {
              id: data.id,
              firstName: data.firstName,
              lastName: data.lastName,
              userId: data.id,
            },
          ]);
          setMessageModal({ open: true, type: 'standard', shouldSessionDataBeHide: true });
        },
      });
    }

    if (hasAdminUserDeactivationPermission) {
      items.push({
        text: 'Usuń konto użytkownika',
        onClick: () => {
          setIsUserDeleteConfirmationModal(true);
          setSelectedUserId(data.id);
        },
      });
    }

    return <ActionsMenu menuItems={items} rowIndex={rowIndex} />;
  };

  const onExaminerActionsClick = (id: string) => {
    setIsExaminerEdit(false);
    setSelectedExaminerId(id);
    setOpenExaminerModal(true);
  };

  const getExaminerActionsMenuIcon = (data: IUser, rowIndex: number): JSX.Element => {
    const items: ActionMenuItem[] = [];
    items.push({
      text: 'Szczegóły',
      onClick: () => {
        onExaminerActionsClick(data.id);
      },
    });
    if (hasAdminUserCreate || hasAdminExaminerCreatePerm || hasAdminOKEExaminerCreatePerm) {
      items.push({
        text: 'Edycja',
        onClick: () => {
          setIsExaminerEdit(true);
          setSelectedExaminerId(data.id);
          setOpenExaminerModal(true);
        },
      });
    }
    if (hasPermissionToCommunicationModule && user.profile.sub !== data.id) {
      items.push({
        text: 'Wyślij wiadomość',
        onClick: () => {
          setSelectedRecipients([
            {
              id: data.id,
              firstName: data.firstName,
              lastName: data.lastName,
              userId: data.id,
            },
          ]);
          setMessageModal({ open: true, type: 'standard', shouldSessionDataBeHide: true });
        },
      });
    }
    return <ActionsMenu menuItems={items} rowIndex={rowIndex} />;
  };

  useEffect(() => {
    const usersTableData: UsersTableItem[] = users.map((data: IUser, index) => ({
      id: data.id,
      tableEnterId: data.id,
      userId: data.id,
      isBlocked: data.emailConfirmed,
      firstName: data.firstName,
      lastName: data.lastName,
      email: (
        <div style={{ wordWrap: 'break-word' }}>
          {data.email.split('@').map((text: string, index: number) => (
            <span key={text} style={{ display: 'inline-block' }}>
              {index === 1 ? <>@</> : null}
              {text}
            </span>
          ))}
        </div>
      ),
      pesel: data.pesel,
      accountTypeName: getAccountTypeLabel(data.accountTypeId) || '',
      examCommittee: getExamCommitteeNameFromIdString(+data.examCommitteeId),
      phone: formatPhone(data.phone),
      accountStatus: <>{data.emailConfirmed ? 'Aktywne' : 'Nieaktywne'}</>,
      actions: getUserActionsMenuIcon(data, index),
    }));
    setUsersData(usersTableData);
  }, [users]);

  useEffect(() => {
    const examinersTableData: ExaminersTableItem[] = users.map((data: IUser, index) => ({
      id: data.id,
      tableEnterId: data.id,
      userId: data.id,
      email: data.email,
      examCommittee: getExamCommitteeNameFromIdString(+data.examCommitteeId),
      firstName: data.firstName,
      lastName: data.lastName,
      pesel: data.pesel,
      phone: formatPhone(data.phone),
      status: (
        <DotWithLabel color={data.isOnline ? '#347F42' : '#BC0000'} label={data.isOnline ? 'online' : 'offline'} />
      ),
      actions: getExaminerActionsMenuIcon(data, index),
    }));
    setExaminersData(examinersTableData);
  }, [users]);

  const filterElements: { [key: string]: JSX.Element } = {
    accountTypeName: (
      <ItemsFilter
        valueName="accountTypeName"
        valueLabel="Typ konta"
        onChange={handleFilterChange}
        resetFilter={!filter.accountTypeName.length}
        itemsWithValues={[
          { label: 'Administrator', value: AccountTypeEnum.Administrator.toString() },
          { label: 'Egzaminator', value: AccountTypeEnum.Examiner.toString() },
          { label: 'Pracownik Komisji Egzaminacyjnej', value: AccountTypeEnum.ExamCommitteeEmployee.toString() },
        ]}
        filter={filter.accountTypeName}
      />
    ),
    examCommittee: (
      <ItemsFilter
        disabled={
          (examinersRoute && hasAdminOkeExaminerReadPermission && !hasAdminExaminerReadPermission) ||
          (!examinersRoute && hasAdminOkeUserReadPermission && !hasAdminUserReadPermission)
        }
        valueName="examCommittee"
        valueLabel="Komisja"
        onChange={handleFilterChange}
        resetFilter={!filter.examCommittee.length}
        itemsWithValues={convertToFilterLabelItems()}
        filter={filter.examCommittee}
      />
    ),
    accountStatus: (
      <ItemsFilter
        valueName="accountStatus"
        valueLabel="Status konta"
        onChange={handleFilterChange}
        resetFilter={!filter.accountStatus.length}
        itemsWithValues={[
          { label: 'Aktywne', value: 'Aktywne' },
          { label: 'Nieaktywne', value: 'Nieaktywne' },
        ]}
        filter={filter.accountStatus}
      />
    ),
    status: (
      <ItemsFilter
        valueName="status"
        valueLabel="Status"
        onChange={handleFilterChange}
        resetFilter={!filter.status.length}
        itemsWithValues={[
          { label: 'online', value: 'online' },
          { label: 'offline', value: 'offline' },
        ]}
        filter={filter.status}
      />
    ),
  };

  const isItemUpdated = (examiner?: boolean) => {
    if (examiner) {
      setSelectedExaminerId(undefined);
      showLoader(true);
      getExaminers();
    } else {
      setSelectedUserId(undefined);
      setOpenUserDetailsModal(false);
      getUsers();
    }
  };

  const handleUserDetailsClose = () => {
    setSelectedUserId(undefined);
    setOpenUserDetailsModal(false);
  };

  const examinerDialogClose = () => {
    setOpenExaminerModal(false);
    setSelectedExaminerId(undefined);
  };

  const handleDeactivateAllUsers = () => {
    setIsAllUsersDeactivateConfirmationModal(false);
    const filters = getFilterValues();
    showLoader(true);
    deactivateAllUsers(
      pageInfo.pageSize,
      pageInfo.currentPage,
      sortByColumnId,
      isDescending,
      '',
      ...(filters as string[]),
    )
      .then(
        () => {
          openAlert('success');
          getUsers();
        },
        () => {
          openAlert('save_error');
        },
      )
      .finally(() => showLoader(false));
  };

  const handleForcePasswordChangeAllUsers = () => {
    setIsAllUsersForcePasswordChangeConfirmationModal(false);
    const filters = getFilterValues();
    showLoader(true);
    passwordChangeAllUsers(...(filters as string[]))
      .then(
        () => {
          openAlert('success');
          getUsers();
        },
        () => {
          openAlert('send_error');
        },
      )
      .finally(() => showLoader(false));
  };

  const handleDeactivateSelectedUsers = () => {
    setIsUsersDeactivateConfirmationModal(false);
    showLoader(true);
    deactivateSelectedUsers(!selectedUserId ? usersSelection.items : [selectedUserId])
      .then(
        () => {
          openAlert('success');
          getUsers();
        },
        () => {
          openAlert('save_error');
        },
      )
      .finally(() => showLoader(false));
  };
  const handleUserDeleteProcessStart = () => {
    setIsUserDeleteConfirmationModal(false);
    showLoader(true);
    if (selectedUserId) {
      deleteUser(selectedUserId)
        .then((response) => {
          if (response.data === UserDeleteStatus.Success) {
            openAlert('success');
          } else if (response.data === UserDeleteStatus.AlreadyAssignedToSession) {
            openAlert(
              'send_error',
              ' Nie możesz usunąć konta użytkownika. Został on przypisany do sesji egzaminacyjnej',
            );
          } else {
            openAlert('send_error');
          }
        })
        .finally(() => showLoader(false));
    }
  };

  const handleForcePasswordChangeSelectedUsers = () => {
    setIsForceUsersPasswordChangeConfirmationModal(false);
    showLoader(true);
    forcePasswordChangeSelectedUsers(!selectedUserId ? usersSelection.items : [selectedUserId])
      .then(
        () => {
          openAlert('success');
          getUsers();
        },
        () => {
          openAlert('send_error');
        },
      )
      .finally(() => showLoader(false));
  };

  const handleDeactivateUsers = () => {
    usersSelection.isAll ? setIsAllUsersDeactivateConfirmationModal(true) : setIsUsersDeactivateConfirmationModal(true);
  };

  const forceUsersPasswordsChange = () => {
    usersSelection.isAll
      ? setIsAllUsersForcePasswordChangeConfirmationModal(true)
      : setIsForceUsersPasswordChangeConfirmationModal(true);
  };

  const isUserSelectedHimself = (): boolean => {
    return usersSelection.items.length === 1 && usersSelection.items[0] === user.profile.sub;
  };

  const getSelectedUsersActionsMenuIcon = (): JSX.Element => {
    const items: ActionMenuItem[] = [];
    if (hasAdminUserDeactivationPermission) {
      items.push({
        text: 'Ponowna aktywacja konta',
        disabled: false,
        onClick: () => handleDeactivateUsers(),
      });
    }
    items.push({
      text: 'Wymuś zmianę hasła',
      disabled: false,
      onClick: () => forceUsersPasswordsChange(),
    });
    if (hasPermissionToCommunicationModule && !isUserSelectedHimself()) {
      items.push({
        text: 'Wyślij wiadomość',
        disabled: false,
        onClick: () => {
          const selectedUsers = Object.values(selectedUsersData.current)
            .filter((e) => usersSelection.items.includes(e.id) && e.id !== user.profile.sub)
            .map((e) => {
              return {
                firstName: e.firstName,
                lastName: e.lastName,
                id: e.id,
              };
            });
          setSelectedRecipients(selectedUsers);
          setMessageModal({ open: true, type: 'standard', shouldSessionDataBeHide: true });
        },
      });
    }
    return (
      <ActionsMenu
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        menuItems={items}
        iconStyle={{ zIndex: 100, bottom: '2px', left: '-5px' }}
      />
    );
  };

  const getSelectedExaminersActionsMenuIcon = (): JSX.Element => {
    const items: ActionMenuItem[] = [];
    items.push({
      text: 'Wyślij wiadomość',
      disabled: false,
      onClick: () => {
        const selectedExaminers = Object.values(selectedUsersData.current)
          .filter((e) => usersSelection.items.includes(e.id) && e.id !== user.profile.sub)
          .map((e) => {
            return {
              firstName: e.firstName,
              lastName: e.lastName,
              id: e.id,
            };
          });

        setSelectedRecipients(selectedExaminers);
        setMessageModal({ open: true, type: 'standard', shouldSessionDataBeHide: true });
      },
    });

    return (
      <ActionsMenu
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        menuItems={items}
        iconStyle={{ zIndex: 100, bottom: '2px', left: '-5px' }}
      />
    );
  };

  return (
    <>
      <Layout viewType={'admin'}>
        <div className={classes.tableAndBreadcrumContainer}>
          <div
            className={`${classes.adminBreadcrumbHeader} ${
              examinersRoute
                ? classes.adminBreadcrumbHeaderWithButtonsExaminers
                : classes.adminBreadcrumbHeaderWithButtonsUsers
            }`}
          >
            <Breadcrumbs
              listRoute={examinersRoute ? Paths.examiners : Paths.users}
              currentList={examinersRoute ? t('global:sidebar.examiners') : t('global:sidebar.users')}
            />
            <div
              className={`${classes.adminButtonsContainer} ${
                examinersRoute
                  ? classes.adminButtonsContainerResponsiveExaminers
                  : classes.adminButtonsContainerResponsiveUsers
              }`}
            >
              {(hasAdminUserReadPermission ||
                hasAdminOkeUserReadPermission ||
                hasAdminExaminerReadPermission ||
                hasAdminOkeExaminerReadPermission) && (
                <OutlinedButton
                  className={classes.exportButton}
                  startIcon={<SaveAltIcon className={classes.exportButtonIcon} />}
                  onClick={() => exportUsers(examinersRoute)}
                >
                  Eksportuj
                </OutlinedButton>
              )}
              {examinersRoute ? <ExaminerCreate userUpdated={isItemUpdated} /> : <UserCreate userAdded={userAdded} />}
              {examinersRoute && (hasAdminExaminerCreatePerm || hasAdminOKEExaminerCreatePerm) && <ImportExaminers />}
            </div>
          </div>
          <div className={classes.tableWhiteCardFlex}>
            <div className={classes.filtersContainerFlex}>
              <div className={classes.usersHeaderContainer}>
                <Typography variant={'h1'} className={classes.gridHeader}>
                  {examinersRoute ? t('examiners:header.title') : t('users:header.title')}
                </Typography>
                {usersSelection.items.length > 0 && (
                  <div className="usersCounterContainer">
                    <SelectedRecordCountDisplay selection={usersSelection} totalItems={pageInfo?.totalCount} />
                    <TooltipOne title="Więcej" placement="top">
                      <>
                        {examinersRoute && hasPermissionToCommunicationModule
                          ? getSelectedExaminersActionsMenuIcon()
                          : getSelectedUsersActionsMenuIcon()}
                      </>
                    </TooltipOne>
                  </div>
                )}
              </div>
              <Pagination
                className={classes.adminPaginationFlex}
                pageInfo={pageInfo}
                getPage={getPages}
                isLoader={isLoader}
              />
            </div>
            <div
              className={[
                classes.adminTableContainerFlex,
                isSidebarMinimazed ? classes.adminTableContainerFullWidth : classes.adminTableContainerNarrowed,
              ].join(' ')}
            >
              {examinersRoute && (
                <SelectableTable<ExaminersTableItem, ExaminersSortBy>
                  columns={examinersColumns}
                  rows={examinersData}
                  onFilterChange={handleFilterChange}
                  filter={filter}
                  filterElements={filterElements}
                  isMultiChoice={true}
                  closeFilterItem={uncheckUsersFilterItem}
                  uncheckAllFilters={uncheckAllUsersFilters}
                  filterbar={filterState}
                  sortByProperty={sortExaminersByColumnId}
                  isDescending={isDescending}
                  setIsDescending={setIsDescending}
                  setSortByColumn={setSortExaminersByColumnId}
                  onSelectionChanged={setUsersSelection}
                  onEnterMethod={onExaminerActionsClick}
                  caption={t('examiners:header.title')}
                />
              )}
              {!examinersRoute && (
                <SelectableTable<UsersTableItem, UsersSortBy>
                  columns={usersColumns}
                  rows={usersData}
                  onFilterChange={handleFilterChange}
                  filter={filter}
                  filterElements={filterElements}
                  isMultiChoice={true}
                  closeFilterItem={uncheckUsersFilterItem}
                  uncheckAllFilters={uncheckAllUsersFilters}
                  filterbar={filterState}
                  sortByProperty={sortByColumnId}
                  isDescending={isDescending}
                  setIsDescending={setIsDescending}
                  setSortByColumn={setSortByColumnId}
                  onSelectionChanged={setUsersSelection}
                  withHighFilterRow={true}
                  onEnterMethod={onUsersActionsClick}
                  caption={t('users:header.title')}
                />
              )}
            </div>
          </div>
        </div>
      </Layout>
      {selectedUserId && openUserDetailsModal && (
        <UserDetails id={selectedUserId} isUserUpdated={isItemUpdated} onClose={handleUserDetailsClose} />
      )}
      {roleData !== undefined && (
        <RolesForUser userId={roleData.id} userName={roleData.userName} onClose={() => setRoleData(undefined)} />
      )}
      {selectedExaminerId && openExaminerModal && (
        <ExaminerDialog
          id={selectedExaminerId}
          isUserUpdated={() => isItemUpdated(true)}
          onClose={examinerDialogClose}
          isEdit={isExaminerEdit}
          isOpen={openExaminerModal}
        />
      )}
      <Prompt
        type="confirmation"
        open={isUsersDeactivateConfirmationModal}
        mainText={
          usersSelection.items.length > 1
            ? `Czy na pewno chcesz umożliwić ponowną aktywację konta dla ${
                usersSelection.isAll ? pageInfo.totalCount : usersSelection.items.length
              } z ${pageInfo.totalCount} użytkowników?`
            : 'Czy na pewno chcesz umożliwić ponowną aktywację konta użytkownikowi?'
        }
        mainActionButtonText="TAK"
        onConfirm={() => {
          handleDeactivateSelectedUsers();
        }}
        onCancel={() => {
          setIsUsersDeactivateConfirmationModal(false);
        }}
      />
      <Prompt
        type="confirmation"
        isBold={true}
        open={isUserDeleteConfirmationModal}
        mainText={'Czy na pewno chcesz usunąć użytkownika? Nie będzie możliwe przywrócenie tej operacji.'}
        mainActionButtonText="TAK"
        onConfirm={() => {
          handleUserDeleteProcessStart();
        }}
        onCancel={() => {
          setIsUserDeleteConfirmationModal(false);
        }}
      />
      <Prompt
        type="confirmation"
        open={isForceUsersPasswordChangeConfirmationModal}
        mainText={
          usersSelection.items.length > 1
            ? `Czy na pewno chcesz wywołać konieczność zmiany hasła dla ${
                usersSelection.isAll ? pageInfo.totalCount : usersSelection.items.length
              } z ${pageInfo.totalCount} użytkowników?`
            : 'Czy na pewno chcesz wywołać konieczność zmiany hasła użytkownikowi?'
        }
        mainActionButtonText="TAK"
        onConfirm={() => {
          handleForcePasswordChangeSelectedUsers();
        }}
        onCancel={() => {
          setIsForceUsersPasswordChangeConfirmationModal(false);
        }}
      />
      <Prompt
        type="warning"
        open={isAllUsersDeactivateConfirmationModal}
        mainText={'Czy na pewno chcesz umożliwić ponowną aktywację konta wszystkim użytkownikom?'}
        mainActionButtonText="TAK"
        onConfirm={() => {
          handleDeactivateAllUsers();
        }}
        onCancel={() => {
          setIsAllUsersDeactivateConfirmationModal(false);
        }}
      />
      <Prompt
        type="warning"
        open={isAllUsersForcePasswordChangeConfirmationModal}
        mainText={'Czy na pewno chcesz wywołać konieczność zmiany hasła wszystkim użytkownikom?'}
        mainActionButtonText="TAK"
        onConfirm={() => {
          handleForcePasswordChangeAllUsers();
        }}
        onCancel={() => {
          setIsAllUsersForcePasswordChangeConfirmationModal(false);
        }}
      />
      <NewMessageModal
        open={messageModal.open}
        type={messageModal.type}
        shouldSessionDataBeHide={messageModal.shouldSessionDataBeHide}
        onClose={() => {
          setSelectedRecipients(undefined);
          setMessageModal({ open: false, type: 'standard', shouldSessionDataBeHide: false });
        }}
        onSend={() => setMessageModal({ open: false, type: 'standard', shouldSessionDataBeHide: false })}
        recipient={selectedRecipients}
        isAllPagesItemChecked={usersSelection.isAll}
        countOfAllCheckedRecipients={pageInfo.totalCount}
        userFilters={!examinersRoute ? filter : { ...filter, accountTypeName: ['Egzaminator'] }}
      ></NewMessageModal>

      {openAddUserPassword && selectedUserForPasswordActivation && (
        <EditUserPasswordByAdmin
          open={openAddUserPassword}
          onNavBack={() => handleAddUserPasswordClose()}
          onClose={() => handleAddUserPasswordClose()}
          title="Aktywuj ręcznie konto użytkownika"
          userId={selectedUserForPasswordActivation.id}
          name={selectedUserForPasswordActivation.firstName}
          surname={selectedUserForPasswordActivation.lastName}
          userEmail={selectedUserForPasswordActivation.email}
          isUserUpdated={isItemUpdated}
        />
      )}
    </>
  );
};

export default Users;
